import type { QueryClient } from '@tanstack/react-query';
import { ACTIVE_SUPPLY_POINT_STORAGE_KEY } from '@/features/supply-points/config';
import type { SupplyPoint } from '@/features/supply-points/services/schemas';
import { supplyPointQueries } from '@/features/supply-points/queries';

/** Reads and parses the persisted active supply point ID from localStorage. */
export function getStoredSupplyPointId(): number | null {
  const stored = localStorage.getItem(ACTIVE_SUPPLY_POINT_STORAGE_KEY);
  if (!stored) return null;
  const parsed = parseInt(stored, 10);
  return isNaN(parsed) ? null : parsed;
}

/**
 * Fetches the supply point list and returns the currently active supply point,
 * or `undefined` if no supply points exist. Intended for use in route loaders.
 */
export async function getActiveSupplyPoint(
  queryClient: QueryClient
): Promise<SupplyPoint | undefined> {
  const supplyPoints = await queryClient.ensureQueryData(supplyPointQueries.list());
  const activeId = resolveActiveSupplyPointId(supplyPoints, getStoredSupplyPointId());
  return supplyPoints.find((sp) => sp.id === activeId);
}

/**
 * Returns the ID of the active supply point given the full list and the stored
 * preference. Falls back to the first supply point when the stored ID is absent
 * or no longer valid. Returns `null` when the list is empty.
 */
export function resolveActiveSupplyPointId(
  supplyPoints: SupplyPoint[],
  storedId: number | null
): number | null {
  if (supplyPoints.length === 0) return null;
  const found = storedId !== null ? supplyPoints.find((sp) => sp.id === storedId) : undefined;
  return (found ?? supplyPoints[0]).id;
}
